import React, { createContext, useContext, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, AlertCircle, X, Info, AlertTriangle } from 'lucide-react';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  // Memoized removal function to prevent dependency cycles
  const removeNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  // Function to add a notification
  const notify = useCallback((type, message) => {
    const id = Date.now() + Math.random(); // Ensure unique ID even if triggered rapidly
    setNotifications((prev) => [...prev, { id, type, message }]);

    // Auto-remove after 4 seconds
    setTimeout(() => {
      removeNotification(id);
    }, 4000);
  }, [removeNotification]);

  return (
    <NotificationContext.Provider value={{ notify }}>
      {children}
      
      {/* Toast Container (Fixed to Top-Right, sits above everything) */}
      <div className="fixed top-24 right-4 z-[9999] flex flex-col gap-3 pointer-events-none">
        <AnimatePresence mode="popLayout">
          {notifications.map(({ id, type, message }) => (
            <motion.div
              key={id}
              initial={{ opacity: 0, x: 50, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 50, scale: 0.9 }}
              layout
              className={`pointer-events-auto min-w-[300px] max-w-sm flex items-start p-4 rounded-xl shadow-2xl border-l-4 backdrop-blur-md transition-colors ${
                type === 'success' ? 'bg-white/95 border-green-500 text-green-800' : 
                type === 'error' ? 'bg-white/95 border-red-500 text-red-800' :
                type === 'warning' ? 'bg-white/95 border-orange-500 text-orange-800' :
                'bg-white/95 border-blue-500 text-blue-800'
              }`}
            >
              <div className="mr-3 mt-0.5 shrink-0">
                {type === 'success' && <CheckCircle size={20} />}
                {type === 'error' && <AlertCircle size={20} />}
                {type === 'warning' && <AlertTriangle size={20} />}
                {type === 'info' && <Info size={20} />}
              </div>
              
              <div className="flex-1">
                <p className="font-bold text-sm capitalize mb-0.5">{type}</p>
                <p className="text-sm opacity-90 leading-snug">{message}</p>
              </div>

              <button 
                onClick={() => removeNotification(id)}
                className="ml-3 text-current opacity-50 hover:opacity-100 hover:bg-black/5 rounded-full p-1 transition-all"
              >
                <X size={16} />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </NotificationContext.Provider>
  );
};